<template>
  <v-card>
    <v-toolbar dark color="blue darken-3" elevation="0" flat>
      <span v-if="isRegionMapType">Регионы</span>
      <v-btn
        v-if="!isRegionMapType && (hasLayerPermission('geometry-write')
          || hasGlobalUserPermissions(['layer-data-create', 'layer-data-edit']))"
        color="primary"
        dark
        class="elevation-0"
        @click="$dialog.show('new-object-geometry-dialog', { isEdit: false, })"
      >
        <v-icon>add</v-icon>
      </v-btn>

      <data-import-dialog
        v-if="!isRegionMapType && (hasLayerPermission('geometry-write')
          || hasGlobalUserPermissions(['layer-data-edit', 'layer-export']))"
        :object-id="selectedLayer.id"
        :property-keys="selectedLayer.form ? selectedLayer.form.fields : []"
        class="ml-2"
        label="Импорт данных в слой"
        @import-done="importDone"
      />

      <data-export-dialog
        v-if="!isRegionMapType"
        v-permission="['layer-export']"
        class="ml-2"
        :object-id="selectedLayer.id"
      />

      <div class="flex-grow-1" />

      <region-filter
        v-if="!isRegionMapType"
        :is-loading="isGeometryTableLoading"
        @selected="filterTable"
      />

      <geometry-properties-filter
        v-if="!isRegionMapType"
        :fields="selectedLayer.form ? selectedLayer.form.fields : []"
        :is-loading="isGeometryTableLoading"
        @change="onGeometryPropertiesFilterChange"
      />

      <v-btn
        v-if="selectedLayer.mapType === 'polygon'"
        text
        @click="additionalToolbar = !additionalToolbar"
      >
        <v-icon>keyboard_arrow_down</v-icon>
      </v-btn>
    </v-toolbar>
    <!-- search -->

    <v-toolbar dark color="blue darken-3" elevation="0" flat>
      <v-text-field
        v-model="tableSearch"
        :disabled="isGeometryTableLoading"
        append-icon="search"
        label="Поиск"
        class="ml-2"
        single-line
        hide-details
        @keydown.enter="filterTable"
      />
      <v-btn
        class="ml-4 primary"
        elevation="0"
        :disabled="isGeometryTableLoading"
        @click="filterTable"
      >
        Найти
      </v-btn>
    </v-toolbar>

    <v-toolbar
      v-if="additionalToolbar"
      dark
      color="blue darken-3"
      elevation="0"
      flat
    >
      <v-text-field
        :value="area"
        label="Площадь, м²"
        class="ml-2 mr-2"
        single-line
        @input="inputArea"
      />
    </v-toolbar>

    <v-data-table
      v-model="selectedRows"
      :loading="isGeometryTableLoading"
      :show-select="canSelectRows"
      :options.sync="options"
      :headers="headers"
      :items="list"
      multi-sort
      item-key="id"
      hide-default-footer
      loading-text="Загрузка данных"
      :no-data-text="noDataText"
      @input="selectRows"
    >
      <template
        v-for="field in quickEditFields"
        :slot="['item.properties', field.id].join('.')"
        slot-scope="{ item }"
      >
        <quick-edit-property
          v-if="hasLayerPermission('geometry-write')
            || hasGlobalUserPermissions(['layer-data-edit'])"
          :key="field.id"
          :field="field"
          :property-value="item.properties[field.id]"
          @update="quickEditSave(item.id, field.id, $event)"
        />
        <span v-else :key="field.id">{{ item.properties[field.id] }}</span>
      </template>

      <template v-if="isRegionMapType" #item.value="{ item }">
        {{ regionValueText(item) }}
      </template>

      <template
        v-if="isRegionMapType && (hasLayerPermission('geometry-write')
          || hasGlobalUserPermissions(['layer-data-edit']))"
        #item.additionalText="{ item }"
      >
        <v-btn
          small
          :class="{ success: changedRegionValues.includes(item.id) }"
          @click="openRegionSettingsDialog(item)"
        >
          Редактировать
        </v-btn>
      </template>

      <template #item.location="{ item }">
        <template v-if="selectedLayer.mapType === 'point'">
          {{ item.location.join() }}
        </template>
      </template>

      <template #item.dateTime="{ item }">
        <quick-edit-happened-at
          v-if="hasLayerPermission('geometry-write')
            || hasGlobalUserPermissions(['layer-data-edit'])"
          :happened-at="item.happenedAt"
          @update="quickEditSaveHappenedAt(item.id, $event)"
        />
        <span v-else>{{ getFormatDate(item.happenedAt.date) }}</span>
      </template>

      <template #item.actions="{ item }">
        <div class="d-flex flex-column py-1 justify-end align-end" style="max-width: 100%">
          <div class="v-item-group theme--light v-btn-toggle">
            <v-btn
              v-if="['linestring', 'polygon', 'point'].includes(selectedLayer.mapType)"
              small
              outlined
              @click="onMapElementShownClick(item.id)"
            >
              <v-icon color="primary">map</v-icon>
            </v-btn>

            <v-btn
              v-if="!isRegionMapType && (hasLayerPermission('geometry-write')
                || hasGlobalUserPermissions(['layer-data-edit']))"
              small
              outlined
              @click="$dialog.show('new-object-geometry-dialog', {
                isEdit: true,
                item: original(item.id),
                mapType: selectedLayer.mapType,
              })"
            >
              <v-icon>edit</v-icon>
            </v-btn>

            <v-btn
              v-if="!isRegionMapType && (hasLayerPermission('geometry-delete')
                || hasGlobalUserPermissions(['layer-data-delete']))"
              small
              outlined
              @click="$dialog.show('remove-geometry-confirmation', { itemId: item.id })"
            >
              <v-icon color="error">delete</v-icon>
            </v-btn>
          </div>
        </div>
      </template>

      <template v-if="pagination && !isRegionMapType" #footer>
        <div class="geometry-table-footer">
          <div v-if="canSelectRows && selectedRows.length" class="selected-rows-actions">
            {{ selectedRowsText }}
            <div class="v-item-group theme--light v-btn-toggle">
              <v-btn
                v-if="hasLayerPermission('geometry-write')
                  || hasGlobalUserPermissions(['layer-data-edit'])"
                outlined
                small
                @click="$dialog
                  .show('new-object-geometry-dialog', { isEditMany: true, items: selectedRows })"
              >
                <v-icon>edit</v-icon>
              </v-btn>
              <v-btn
                v-if="hasLayerPermission('geometry-delete')
                  || hasGlobalUserPermissions(['layer-data-delete'])"
                outlined
                small
                @click="$dialog.show('remove-geometry-confirmation', { itemId: selectedRowsId })"
              >
                <v-icon color="error">delete</v-icon>
              </v-btn>
            </div>
          </div>

          <table-footer
            :pagination="pagination"
            :total-visible="8"
            @onChangePage="onChangePage"
            @changePerPage="changePerPage"
          />
        </div>
      </template>
    </v-data-table>

    <remove-geometry-confirmation @confirmed="onGeometryRemove" />
    <object-element-viewing-dialog
      :object-id="selectedLayer.id"
      :map-type="selectedLayer.mapType"
    />

    <region-layer-settings-dialog
      v-if="isRegionMapType"
      @update="onRegionValueListChanged"
    />

    <new-object-geometry-dialog
      v-if="hasLayerPermission('geometry-write') || hasGlobalUserPermissions(['layer-data-create'])"
      :property-keys="selectedLayer.form ? selectedLayer.form.fields : []"
      :map-type="selectedLayer.mapType"
      :object-id="selectedLayer.id"
      object-type="layers"
      @created="onObjectCreated"
    />
  </v-card>
</template>

<script>
import moment from 'moment';
import get from 'lodash/get';
import each from 'lodash/each';
import debounce from 'lodash/debounce';
import { mapState, mapActions } from 'vuex';
import GeometryPropertiesFilter from '@/components/page/geometry-properties-filter';
import DataExportDialog from '@/components/dialogs/DataExportDialog.vue';
import RegionFilter from '@/components/page/region-filter';
import DataImportDialog from '@/components/dialogs/DataImportDialog.vue';
import NewObjectGeometryDialog from '@/components/dialogs/NewObjectGeometryDialog.vue';
import RemoveGeometryConfirmation from '@/views/layers/components/RemoveGeometryConfirmation.vue';
import ObjectElementViewingDialog from '@/components/dialogs/map/ObjectElementViewingDialog.vue';
import QuickEditProperty from '@/views/layers/components/QuickEditProperty.vue';
import QuickEditHappenedAt from '@/views/layers/components/QuickEditHappenedAt.vue';
import RegionLayerSettingsDialog from '@/components/dialogs/RegionLayerSettingsDialog/RegionLayerSettingsDialog.vue';
import { declensionOfNumerals } from '@/utils/string';
import camelCase from 'lodash/camelCase';
import TableFooter from '@/components/page/table-footer';

export default {
  name: 'geometry-table',

  components: {
    GeometryPropertiesFilter,
    DataExportDialog,
    RegionFilter,
    DataImportDialog,
    NewObjectGeometryDialog,
    RemoveGeometryConfirmation,
    ObjectElementViewingDialog,
    QuickEditProperty,
    QuickEditHappenedAt,
    RegionLayerSettingsDialog,
    TableFooter,
  },

  props: {
    id: {
      type: String,
      required: true,
    },
    valueList: {
      type: Array,
      required: true,
    },
    selectedLayer: {
      type: Object,
      required: true,
    },
    layerSettings: {
      type: Object,
      default: () => ({}),
    },
    layerPermissions: {
      type: Object,
      default: () => ({}),
    },
  },

  data: () => ({
    selectedRegionsList: [],
    changedRegionValues: [],
    isGeometryTableLoading: false,
    additionalToolbar: false,
    propertiesFilters: null,
    selectedRows: [],
    noDataText: 'Слой не содержит данных',
    options: {
      sortBy: ['dateTime'],
      sortDesc: [true],
    },
    area: null,
    tableSearch: '',
    geometryHeaders: [
      {
        value: 'dateTime',
        text: 'Дата и время',
        sortable: true,
        skip: true,
      },
      {
        value: 'actions',
        sortable: false,
        width: '22%',
        skip: true,
      },
    ],
  }),

  computed: {
    ...mapState('layer-objects/geometries/search', {
      searchGeometryList: 'list',
      pagination: 'pagination',
    }),

    ...mapState('filters', [
      'selectedRegions',
    ]),

    ...mapState({
      userPermissions: state => state.auth.permissions,
    }),

    hasLayerPermission() {
      return (permission) => this.layerPermissions[permission];
    },

    isRegionMapType() {
      return this.selectedLayer.mapType === 'region';
    },

    hasGlobalUserPermissions() {
      return (permissions) => permissions.some((_) => this.userPermissions.includes(camelCase(_)));
    },

    selectedRowsText() {
      const count = this.selectedRows.length;
      const selectedText = declensionOfNumerals(
        count,
        ['Выбран', 'Выбрано', 'Выбрано'],
      );
      const elText = declensionOfNumerals(
        count,
        ['элемент', 'элемента', 'элементов'],
      );
      return `${selectedText} ${count} ${elText}`;
    },

    fieldHeaders() {
      const fields = get(this.selectedLayer, 'form.fields', []);
      return fields.map(_ => ({
        value: `properties.${_.id}`,
        text: _.name,
        sortable: true,
      }));
    },

    quickEditFields() {
      const fields = get(this.selectedLayer, 'form.fields', []);
      return fields.map((field) => ({
        id: field.id,
        name: field.name,
        isSelect: field.type === 'select',
        values: field.type === 'select' ? field.preparedValues : null,
      }));
    },

    headers() {
      if (this.isRegionMapType) {
        return [
          { text: 'Регион', value: 'region.name', sortable: true },
          { text: 'Значение', value: 'value', sortable: false },
          {
            text: '', value: 'additionalText', sortable: false, width: '12%',
          },
        ];
      }

      return [...this.fieldHeaders, ...this.geometryHeaders];
    },

    canSelectRows() {
      return !this.isRegionMapType
      && (['layer-data-edit', 'layer-data-delete'].some(_ => this.userPermissions.includes(camelCase(_)))
      || this.layerPermissions['geometry-write']);
    },

    list() {
      return this.listWithGoodProps(this.searchGeometryList);
    },

    selectedRowsId() {
      return this.selectedRows.map(_ => _.id);
    },

    valueListObject() {
      const object = {};

      this.valueList.forEach(({ id, text }) => {
        object[id] = text || '---';
      });

      return object;
    },


    original() {
      return (id) => this.searchGeometryList.find(_ => _.id === id);
    },

    listWithGoodProps() {
      return (list) => (list || []).map(_ => {
        const properties = {};
        each(_.properties, (item) => {
          properties[item.field.id] = item.value;
        });
        return {
          ..._,
          properties,
        };
      });
    },
  },

  watch: {
    // eslint-disable-next-line func-names
    propertiesFilters: debounce(async function () {
      this.isGeometryTableLoading = true;
      await this.filterTable();
      this.isGeometryTableLoading = false;
    }, 50),

    // eslint-disable-next-line func-names
    area: debounce(async function () {
      this.isGeometryTableLoading = true;
      await this.filterTable();
      this.isGeometryTableLoading = false;
    }, 750),

    options() {
      this.filterTable();
    },
  },

  created() {
    this.options.itemsPerPage = this.isRegionMapType ? 100 : 12;
  },

  mounted() {
    this.selectedRegionsList = this.selectedRegions;
  },

  methods: {
    ...mapActions(['showNotify']),
    ...mapActions('layer-objects/geometries', {
      removeGeometry: 'remove',
      updateGeometry: 'update',
      removeManyGeometry: 'removeMany',
      getRegionDataList: 'getRegionDataList',
    }),

    ...mapActions('layer-objects/geometries/search', {
      searchGetList: 'getList',
      searchGetNextPage: 'getNextPage',
    }),

    openRegionSettingsDialog(item) {
      this.$dialog.show('region-layer-settings-dialog', {
        item, single: this.selectedLayer, valueList: this.valueList,
      });
    },

    getFormatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD') : '---';
    },

    inputArea(value) {
      this.area = value;
    },

    handleError(e) {
      const defaultMessage = e.message || 'Что-то пошло не так';
      const dataMessage = e.response?.data?.data?.message;

      if (dataMessage && dataMessage.includes('geometry-read')) {
        this.noDataText = 'Недостаточно прав для чтения объектов';
      }

      this.showNotify({ type: 'error', message: dataMessage || defaultMessage });
    },

    async onRegionValueListChanged({
      value, id, text, data,
    }) {
      this.changedRegionValues.push(id);
      try {
        await this.$store.dispatch('layer-objects/geometries/updateRegionData', {
          objectId: this.selectedLayer.id,
          id,
          value,
          text,
          data,
        });

        await this.$store.dispatch('layer-objects/geometries/getRegionDataList', { objectId: this.selectedLayer.id });
      } catch (e) {
        this.handleError(e);
      }
    },

    getSortById() {
      return this.options.sortBy.map(_ => {
        if (_ === 'dateTime') {
          return 'happened_at';
        }
        return _.split('.')[1];
      });
    },

    async filterTable() {
      try {
        this.isGeometryTableLoading = true;
        await this.searchGetList(this.getSearchParams());
      } catch (e) {
        this.handleError(e);
        console.error(e);
      } finally {
        this.isGeometryTableLoading = false;
      }
    },

    getSearchParams() {
      const { sortDesc, itemsPerPage } = this.options;
      const sortBy = this.getSortById();

      return {
        perPage: itemsPerPage,
        objectId: this.selectedLayer.id,
        sortBy,
        sortDesc,
        ...(this.area && { area: this.area }),
        ...(this.tableSearch?.length && { query: this.tableSearch }),
        ...(this.selectedRegions?.length && { regionIds: this.selectedRegions }),
        mapType: this.selectedLayer.mapType,
        properties: this.propertiesFilters,
      };
    },

    async importDone() {
      await this.getGeometries();
      // eslint-disable-next-line no-unused-expressions
      this.$refs.importTab?.doUpdate();
    },

    async getGeometries() {
      if (this.isRegionMapType) {
        await this.filterTable();
      } else {
        await this.searchGetList(this.getSearchParams(), this.id);
      }
    },

    async onObjectCreated() {
      this.isGeometryTableLoading = true;
      await this.getGeometries();
      this.selectedRows = [];
      this.isGeometryTableLoading = false;
    },

    async quickEditSave(id, fieldId, value) {
      const properties = { [fieldId]: value };
      const data = { id, properties, objectId: this.selectedLayer.id };

      try {
        await this.updateGeometry(data);
      } catch (e) {
        this.handleError(e);
      }
    },

    async quickEditSaveHappenedAt(id, value) {
      const data = {
        id,
        objectId: this.selectedLayer.id,
        happenedAt: value,
      };
      try {
        await this.updateGeometry(data);
      } catch (e) {
        this.handleError(e);
      }
    },

    selectRows(rows) {
      this.selectedRows = rows;
    },

    regionValueText({ value }) {
      if (!this.valueListObject[value]) return '---';
      return this.valueListObject[value];
    },

    async onChangePage(page) {
      try {
        this.isGeometryTableLoading = true;
        await this.searchGetNextPage({
          id: this.selectedLayer.id,
          page,
          ...this.getSearchParams(),
        });
      } catch (error) {
        console.error(error);
      } finally {
        this.isGeometryTableLoading = false;
      }
    },

    async changePerPage(perPage) {
      this.options.itemsPerPage = perPage;
    },

    async onGeometryRemove(geometryId) {
      this.isGeometryTableLoading = true;
      try {
        let message;
        if (Array.isArray(geometryId)) {
          await this.removeManyGeometry({ objectId: this.selectedLayer.id, geometryId });
          message = 'Элементы успешно удалены';
        } else {
          await this.removeGeometry({ objectId: this.selectedLayer.id, geometryId });
          message = 'Элемент успешно удален';
        }
        await this.filterTable();
        this.showNotify({ message });
        this.selectedRows = [];
      } catch (e) {
        this.handleError(e);
      } finally {
        this.isGeometryTableLoading = false;
      }
    },

    onMapElementShownClick(geometryId) {
      const params = {
        geometryId,
        settings: { ...this.layerSettings },
      };
      params.settings.opacity = 0.5;

      this.$dialog.show('object-element-viewing-dialog', params);
    },

    onGeometryPropertiesFilterChange(filters) {
      this.propertiesFilters = filters;
    },
  },
};
</script>

<style lang="scss" scoped>
.geometry-table-footer {
  display: flex;
}

.table-footer {
  flex: 1;
}

.selected-rows-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  padding: 16px;
}

.selected-rows-actions .v-item-group {
  margin-left: 10px;
}
</style>
